import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { API_BASE } from "../utils/api";

const TYPE_STYLES = {
  deadline: "bg-red-50 border-red-200",
  warning:  "bg-amber-50 border-amber-200",
  success:  "bg-emerald-50 border-emerald-200",
  info:     "bg-blue-50 border-blue-200",
};
const TYPE_ICONS = { deadline: "⏰", warning: "⚠️", success: "✅", info: "ℹ️" };
const TYPE_DOT   = { deadline: "bg-red-500", warning: "bg-amber-500", success: "bg-emerald-500", info: "bg-blue-500" };

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function Notifications() {
  const { token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount,   setUnreadCount]   = useState(0);
  const [loading,       setLoading]       = useState(true);
  const [filter,        setFilter]        = useState("all");

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/notifications/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const markRead = async (id) => {
    await fetch(`${API_BASE}/api/notifications/${id}/read`, { method: "PATCH", headers: { Authorization: `Bearer ${token}` } });
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    await fetch(`${API_BASE}/api/notifications/read-all`, { method: "PATCH", headers: { Authorization: `Bearer ${token}` } });
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnreadCount(0);
  };

  const deleteNotif = async (id) => {
    const removed = notifications.find(n => n.id === id);
    await fetch(`${API_BASE}/api/notifications/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    setNotifications(prev => prev.filter(n => n.id !== id));
    if (removed && !removed.is_read) setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const filtered = filter === "unread" ? notifications.filter(n => !n.is_read)
    : filter === "read" ? notifications.filter(n => n.is_read)
    : notifications;

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <svg className="animate-spin w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
      </svg>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
            <p className="text-sm text-gray-500 mt-0.5">{unreadCount > 0 ? `${unreadCount} unread` : "All caught up!"}</p>
          </div>
          {unreadCount > 0 && (
            <button onClick={markAllRead} className="px-4 py-2 text-sm font-semibold text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors">
              Mark all read
            </button>
          )}
        </div>

        <div className="flex gap-2 mb-5">
          {["all","unread","read"].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold capitalize transition-colors
                ${filter === f ? "bg-blue-700 text-white" : "bg-white text-gray-600 border border-gray-200 hover:border-blue-300"}`}>
              {f}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
            <div className="text-5xl mb-3">🔔</div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">No notifications</h3>
            <p className="text-gray-500 text-sm">{filter === "unread" ? "No unread notifications." : "You're all caught up!"}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(n => (
              <div key={n.id}
                className={`relative flex gap-4 p-4 rounded-2xl border transition-all
                  ${n.is_read ? "bg-white border-gray-100 opacity-75" : (TYPE_STYLES[n.type] || TYPE_STYLES.info)}`}>
                {!n.is_read && <span className={`absolute top-4 right-4 w-2.5 h-2.5 rounded-full ${TYPE_DOT[n.type] || "bg-blue-500"}`} />}
                <div className="flex-shrink-0 text-2xl mt-0.5">{TYPE_ICONS[n.type] || "🔔"}</div>
                <div className="flex-1 min-w-0 pr-4">
                  <h4 className={`font-bold text-sm ${n.is_read ? "text-gray-500" : "text-gray-900"}`}>{n.title}</h4>
                  <p className="text-sm text-gray-500 mt-1 leading-relaxed">{n.message}</p>
                  <p className="text-xs text-gray-400 mt-2">{formatDate(n.created_at)}</p>
                  <div className="flex gap-4 mt-3">
                    {!n.is_read && (
                      <button onClick={() => markRead(n.id)} className="text-xs font-semibold text-blue-600 hover:underline">Mark as read</button>
                    )}
                    <button onClick={() => deleteNotif(n.id)} className="text-xs font-semibold text-red-500 hover:underline">Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
